// src/mixins/page-refresh-mixin.js - 页面数据刷新混入
export default {
  data() {
    return {
      isRefreshing: false,
      refreshHandler: null,
      languageWatcher: null
    }
  },

  created() {
    // 监听语言切换后的刷新事件
    this.refreshHandler = this.handleLanguageRefresh.bind(this)
    if (this.$eventBus) {
      this.$eventBus.$on('language-changed-refresh', this.refreshHandler)
      this.$eventBus.$on('refresh-page-data', this.refreshHandler)
      this.$eventBus.$on('language-switched', this.refreshHandler)
    }
  },

  mounted() {
    // 监听Vuex中的语言状态变化
    if (this.$store) {
      this.languageWatcher = this.$store.watch(
        state => state.user.language,
        (newLang, oldLang) => {
          if (newLang && oldLang && newLang.country !== oldLang.country) {
            this.handleLanguageRefresh({
              from: oldLang.country,
              to: newLang.country,
              source: 'vuex-watcher'
            })
          }
        },
        { deep: true }
      )
    }
  },

  beforeDestroy() {
    // 清理事件监听
    if (this.refreshHandler && this.$eventBus) {
      this.$eventBus.$off('language-changed-refresh', this.refreshHandler)
      this.$eventBus.$off('refresh-page-data', this.refreshHandler)
      this.$eventBus.$off('language-switched', this.refreshHandler)
    }
    
    // 清理Vuex监听器
    if (this.languageWatcher) {
      this.languageWatcher()
      this.languageWatcher = null
    }
  },

  methods: {
    // 处理语言切换后的刷新
    async handleLanguageRefresh(eventData) {
      if (this.isRefreshing) {
        return
      }

      this.isRefreshing = true

      try {
        // 调用页面的刷新方法
        await this.refreshPageData()
      } catch (error) {
        // console.error('❌ Page refresh failed:', error)
      } finally {
        // 延迟重置刷新状态，避免连续刷新
        setTimeout(() => {
          this.isRefreshing = false
        }, 1000)
      }
    },

    // 默认的页面数据刷新方法（子组件可以重写）
    async refreshPageData() {
      const refreshMethods = [
        'refreshData',     // 统一刷新方法
        'getList',         // 获取列表数据
        'getData',         // 获取数据
        'fetchData',       // 拉取数据
        'loadData',        // 加载数据
        'getTypeList',     // 获取分类列表
        'getProjectTypeList', // 获取项目分类列表
        'getBanner',       // 获取轮播图
        'getNewsList',     // 获取新闻列表
        'init',            // 初始化
        'refresh',         // 刷新
        'refreshBreadcrumb' // 刷新面包屑导航
      ]

      let refreshed = false
      
      // 尝试调用常见的数据获取方法
      for (const methodName of refreshMethods) {
        if (typeof this[methodName] === 'function') {
          try {
            await this[methodName]()
            refreshed = true
            // 不要break，可能需要调用多个方法
            if (methodName === 'refreshData') {
              break // 如果有统一刷新方法，就不需要调用其他方法了
            }
          } catch (error) {
            // console.warn(`⚠️ Failed to call ${methodName}():`, error)
          }
        }
      }
      
      // 如果组件有特定的刷新逻辑，调用它
      if (typeof this.onLanguageChanged === 'function') {
        try {
          await this.onLanguageChanged()
          refreshed = true
        } catch (error) {
          // console.warn('⚠️ onLanguageChanged failed:', error)
        }
      }
      
      // 刷新面包屑导航
      if (typeof this.updateBreadcrumb === 'function') {
        try {
          await this.updateBreadcrumb()
          refreshed = true
        } catch (error) {
          // console.warn('⚠️ updateBreadcrumb failed:', error)
        }
      }
      
      if (!refreshed) {
        // console.log('🚨 No refresh methods found, only force updating component')
      }

      // 强制更新组件
      this.$nextTick(() => {
        this.$forceUpdate()
      })
    },

    // 手动触发页面刷新
    async triggerPageRefresh() {
      if (this.$eventBus) {
        this.$eventBus.$emit('refresh-page-data', {
          source: 'manual',
          timestamp: Date.now()
        })
      } else {
        await this.handleLanguageRefresh({ source: 'manual' })
      }
    }
  }
}